import type { Route } from "./+types/root.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import { MixpanelTracking } from "@iyk/tracking"
import AppleTouchIconPngUrl from "@iyk/ui/assets/apple-touch-icon.png?url"
import FaviconSvgUrl from "@iyk/ui/assets/favicon.svg?url"
import { Effect, Option } from "effect"
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router"
import {
  AnalyticsContext,
  consoleAnalytics,
  type Analytics,
} from "../packages/analytics/analytics.ts"
import { MixpanelAnalytics, type Mixpanel } from "../packages/analytics/mixpanel-analytics.tsx"
import { monitoring, SentryMonitoring } from "../packages/monitoring/sentry-monitoring.tsx"
import { Handler } from "../server/handler.ts"
import { Res } from "../server/response.ts"
import { ActivityProvider } from "./activity/use-activity.tsx"
import stylesheet from "./app.css?url"

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "apple-touch-icon", sizes: "180x180", href: AppleTouchIconPngUrl },
  { rel: "icon", type: "image/svg+xml", href: FaviconSvgUrl },
]

export const loader = Handler.make("root", ({ cookies, cookieJar, sessions, mixpanel, sentry }) =>
  Effect.gen(function* () {
    const session = yield* sessions.authenticated.pipe(Effect.option)
    const toast = yield* cookies.toast.parse.pipe(
      Effect.tap(() => cookieJar.unset(cookies.toast)),
      Effect.option,
    )

    return Res.json({
      mixpanel: Option.getOrUndefined(mixpanel),
      user: session.pipe(
        Option.map((session) => ({
          id: session.user.id.value,
          email: session.user.value.email,
          name: session.user.value.name,
          createdAt: session.user.value.createdAt.toISOString(),
          authMethod: session.authMethod,
        })),
        Option.getOrUndefined,
      ),
      sentry: Option.isOption(sentry) ? Option.getOrUndefined(sentry) : sentry,
      toast: Option.getOrNull(toast),
    })
  }),
)

export function Layout({ children }: { children: React.ReactNode }) {
  const { mixpanel } = useLoaderData<typeof loader>()

  return (
    <html lang="en" className="dark bg-gray-1 text-gray-12">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width" />
        <Meta />
        <Links />
        {mixpanel?.token && (
          <MixpanelTracking
            id={mixpanel.token}
            track_pageview="url-with-path-and-query-string"
            autotrack
            persistence="localStorage"
          />
        )}
      </head>
      <body>
        {/* We need to wrap the children in a div to avoid the whole
         content being moved to the left when the sheet is open */}
        <div>{children}</div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  React.useEffect(() => {
    if (loaderData?.toast) {
      UI.toast({
        title: loaderData.toast.message,
        description: loaderData.toast.description,
        variant: loaderData.toast.type === "error" ? "destructive" : "default",
      })
    }
  }, [loaderData?.toast])

  return (
    <AnalyticsProvider user={loaderData.user} token={loaderData.mixpanel?.token}>
      <ActivityProvider>
        <Outlet />
        <UI.Toaster />
        {loaderData.sentry && <SentryMonitoring.ClientScript config={loaderData.sentry} />}
      </ActivityProvider>
    </AnalyticsProvider>
  )
}

const AnalyticsProvider = ({
  children,
  user,
  token,
}: {
  children: React.ReactNode
  user?: Analytics.User
  token?: Mixpanel.Token
}) => {
  return token ? (
    <MixpanelAnalytics.Provider user={user} token={token}>
      {children}
    </MixpanelAnalytics.Provider>
  ) : (
    <AnalyticsContext.Provider value={consoleAnalytics}>{children}</AnalyticsContext.Provider>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "앗!"
  let details = "예기치 않은 오류가 발생했습니다."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "오류"
    details =
      error.status === 404 ? "요청하신 페이지를 찾을 수 없습니다." : error.statusText || details
  } else if (error && error instanceof Error) {
    monitoring.captureException(error)
    if (import.meta.env.DEV) {
      details = error.message
      stack = error.stack
    }
  }

  if (stack) {
    console.error(stack)
  }

  return (
    <main className="min-h-dvh container p-4 mx-auto flex flex-col items-center justify-center text-center">
      <h1 className="text-sm">{message}</h1>
      <p className="text-sm text-gray-11">{details}</p>
    </main>
  )
}
